/**
 * Logica del tema, senza React.
 *
 * Separata dal provider perché deve girare prima che React monti: applicata
 * in {@link main.tsx} sincronamente, prima del primo render, così la pagina
 * non lampeggia nel tema sbagliato per una frazione di secondo.
 */

export type ThemePreference = 'light' | 'dark' | 'system'
export type EffectiveTheme = 'light' | 'dark'

const STORAGE_KEY = 'dashboard:theme'
const PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system']

function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (PREFERENCES as readonly string[]).includes(value)
}

/** La preferenza salvata, o "system" se non ce n'è ancora una. */
export function getStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isThemePreference(stored) ? stored : 'system'
  } catch {
    // Storage bloccato (navigazione privata, criteri aziendali): si procede
    // col default, senza far fallire l'avvio dell'app per una preferenza.
    return 'system'
  }
}

export function storePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    // Niente persistenza in questa sessione: non è un errore da segnalare.
  }
}

function systemPrefersLight(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: light)').matches
}

/** Risolve una preferenza nel tema effettivo, seguendo il sistema quando serve. */
export function resolveTheme(preference: ThemePreference): EffectiveTheme {
  if (preference === 'system') return systemPrefersLight() ? 'light' : 'dark'
  return preference
}

/** Scrive il tema effettivo sul documento. Il CSS fa il resto. */
export function applyTheme(theme: EffectiveTheme): void {
  document.documentElement.dataset.theme = theme
}

/** Applica subito la preferenza salvata. Va chiamata prima del primo render. */
export function applyStoredThemeSync(): void {
  applyTheme(resolveTheme(getStoredPreference()))
}

export function nextPreference(current: ThemePreference): ThemePreference {
  const index = PREFERENCES.indexOf(current)
  return PREFERENCES[(index + 1) % PREFERENCES.length]
}
